/**
 * Engineer Savings Wall — gamified leaderboard
 * ==============================================
 * The viral mechanic that closes the engineering-finance chasm.
 * Anyone in the org can log a savings action; admins verify; everyone
 * sees the podium. The CFO sees who's actually doing the work.
 *
 * Auth-gated. Same brand as the rest of the authed surfaces (slate +
 * emerald accents, tabular-num leaderboard, podium for top 3).
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Trophy, Award, Medal, ArrowLeft, Plus, ShieldCheck, ExternalLink,
  Loader2, Trash2, RefreshCcw,
} from 'lucide-react';
import { toast } from 'sonner';
import WinsWebhookSetup from '../components/WinsWebhookSetup';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_TYPES = [
  { value: 'rightsizing',    label: 'Rightsizing' },
  { value: 'idle_cleanup',   label: 'Idle cleanup' },
  { value: 'commitment_buy', label: 'Commitment buy (SP/RI/CUD)' },
  { value: 'cache',          label: 'Caching' },
  { value: 'model_swap',     label: 'Cheaper model swap' },
  { value: 'tag_fix',        label: 'Tag hygiene' },
  { value: 'other',          label: 'Other' },
];

const fmtMoney = (n) => {
  if (!n && n !== 0) return '$0';
  const a = Math.abs(n);
  if (a >= 1_000_000) return `$${(a / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `$${(a / 1_000).toFixed(1)}K`;
  return `$${Math.round(a)}`;
};

const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

const PODIUM_META = [
  { rank: 1, color: 'from-amber-400 to-yellow-500', bg: 'bg-amber-500/10 border-amber-500/40', icon: Trophy, label: '🥇' },
  { rank: 2, color: 'from-slate-300 to-slate-400', bg: 'bg-slate-500/10 border-slate-400/30', icon: Award, label: '🥈' },
  { rank: 3, color: 'from-orange-400 to-amber-600', bg: 'bg-orange-500/10 border-orange-500/30', icon: Medal, label: '🥉' },
];

const EngineerSavingsWall = () => {
  const [data, setData] = useState(null);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('current'); // 'current' | 'all_time'
  const [verifiedOnly, setVerifiedOnly] = useState(false);

  const [draft, setDraft] = useState({
    engineer_email: '', engineer_name: '', action_type: 'rightsizing',
    description: '', monthly_savings_usd: '', evidence_url: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [lb, acts] = await Promise.all([
        axios.get(`${API}/engineer-savings-wall/leaderboard`, auth()),
        axios.get(`${API}/engineer-savings-wall/actions?limit=20`, auth()),
      ]);
      setData(lb.data);
      setActions(acts.data?.actions || []);
    } catch (e) {
      toast.error("Couldn't load savings wall", { description: e?.response?.data?.detail || e?.message });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, []);

  const submit = async () => {
    if (!draft.engineer_email || !draft.engineer_email.includes('@'))
      return toast.error('Engineer email is required');
    if (!draft.description?.trim() || draft.description.trim().length < 4)
      return toast.error('Describe the action (min 4 chars)');
    if (!draft.monthly_savings_usd || Number(draft.monthly_savings_usd) <= 0)
      return toast.error('Enter the monthly savings ($)');
    setSubmitting(true);
    try {
      const payload = {
        engineer_email: draft.engineer_email.trim(),
        engineer_name: draft.engineer_name?.trim() || undefined,
        action_type: draft.action_type,
        description: draft.description.trim(),
        monthly_savings_usd: Number(draft.monthly_savings_usd),
      };
      if (draft.evidence_url?.trim()) payload.evidence_url = draft.evidence_url.trim();
      await axios.post(`${API}/engineer-savings-wall/actions`, payload, auth());
      toast.success('Action logged · nice save 🎉');
      setDraft({ engineer_email: '', engineer_name: '', action_type: 'rightsizing',
                 description: '', monthly_savings_usd: '', evidence_url: '' });
      load();
    } catch (e) {
      toast.error("Couldn't log action", { description: e?.response?.data?.detail || e?.message });
    } finally { setSubmitting(false); }
  };

  const verify = async (id) => {
    try {
      await axios.patch(`${API}/engineer-savings-wall/actions/${id}/verify`, {}, auth());
      toast.success('Verified · counts toward verified savings');
      load();
    } catch (e) { toast.error("Couldn't verify", { description: e?.response?.data?.detail || e?.message }); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/engineer-savings-wall/actions/${id}`, auth());
      toast.success('Action removed');
      load();
    } catch (e) { toast.error("Couldn't delete", { description: e?.response?.data?.detail || e?.message }); }
  };

  const board = useMemo(() => {
    if (!data) return [];
    let rows = view === 'current' ? data.current_period_leaderboard : data.all_time_leaderboard;
    if (verifiedOnly) {
      // Re-rank using verified savings only — preserves CFO trust:
      // a number she can defend in a board meeting.
      rows = rows
        .filter((r) => (r.verified_count || 0) > 0)
        .map((r) => ({
          ...r,
          monthly_savings_total_usd: r.verified_monthly_savings_usd || 0,
          annualized_savings_total_usd: (r.verified_monthly_savings_usd || 0) * 12,
          actions_count: r.verified_count || 0,
        }))
        .sort((a, b) => b.monthly_savings_total_usd - a.monthly_savings_total_usd);
    }
    return rows;
  }, [data, view, verifiedOnly]);

  const totals = data?.totals || {};

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-6 max-w-6xl" data-testid="engineer-savings-wall">
      <div className="flex items-center gap-3 mb-2">
        <Link to="/dashboard" className="text-sm text-slate-300 hover:text-foreground inline-flex items-center gap-1.5" data-testid="back-link">
          <ArrowLeft className="h-4 w-4" /> Dashboard
        </Link>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Trophy className="w-7 h-7 text-emerald-500" />
            Engineer Savings Wall
          </h1>
          <p className="text-sm text-slate-300 mt-1 max-w-2xl">
            Make saving cloud spend social. Anyone logs a win, an admin verifies, the team sees the leaderboard.
            <br className="hidden sm:block" />
            <span className="text-emerald-500 font-medium">{fmtMoney(totals.all_time_monthly_usd || 0)}/mo</span> saved
            all-time across <span className="text-foreground font-semibold">{totals.engineers_participating || 0}</span> engineer{totals.engineers_participating === 1 ? '' : 's'}.
          </p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading} data-testid="refresh-btn">
          <RefreshCcw className={`w-3.5 h-3.5 mr-1.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </Button>
      </div>

      {/* Totals strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="totals-strip">
        {[
          { label: 'Saved this 30d (monthly run-rate)', val: fmtMoney(totals.last_30d_monthly_usd || 0), accent: 'text-emerald-500' },
          { label: 'All-time monthly savings',          val: fmtMoney(totals.all_time_monthly_usd || 0), accent: 'text-foreground' },
          { label: 'All-time annualized',               val: fmtMoney(totals.all_time_annualized_usd || 0), accent: 'text-emerald-500' },
          { label: 'Actions logged',                    val: (totals.actions_all_time || 0).toLocaleString(), accent: 'text-foreground' },
        ].map((s, i) => (
          <Card key={i}><CardContent className="p-4">
            <p className="text-[11px] uppercase tracking-widest text-slate-300 mb-1">{s.label}</p>
            <p className={`text-2xl font-bold tabular-nums ${s.accent}`}>{s.val}</p>
          </CardContent></Card>
        ))}
      </div>

      {/* Slack wins broadcaster (admin-only — auto-hides for non-admins) */}
      <WinsWebhookSetup />

      {/* Compose */}
      <Card className="border-emerald-500/30">
        <CardHeader><CardTitle className="text-lg flex items-center gap-2"><Plus className="w-5 h-5 text-emerald-500" /> Log a savings win</CardTitle></CardHeader>
        <CardContent className="space-y-3" data-testid="compose-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <Input placeholder="engineer@company.com" value={draft.engineer_email}
              onChange={(e) => setDraft({ ...draft, engineer_email: e.target.value })}
              data-testid="input-engineer-email" />
            <Input placeholder="Display name (optional)" value={draft.engineer_name}
              onChange={(e) => setDraft({ ...draft, engineer_name: e.target.value })}
              data-testid="input-engineer-name" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[1fr,1fr] gap-2">
            <select value={draft.action_type}
              onChange={(e) => setDraft({ ...draft, action_type: e.target.value })}
              className="bg-background border border-input rounded-md h-9 px-2 text-sm"
              data-testid="select-action-type">
              {ACTION_TYPES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
            </select>
            <Input type="number" placeholder="Monthly savings ($/mo)" min={1}
              value={draft.monthly_savings_usd}
              onChange={(e) => setDraft({ ...draft, monthly_savings_usd: e.target.value })}
              data-testid="input-monthly-savings" />
          </div>
          <Input placeholder="What did you do? e.g. Downsized analytics RDS db.r5.4xlarge → db.r5.2xlarge"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            data-testid="input-description" />
          <Input placeholder="Evidence URL (PR / Jira / dashboard) — optional"
            value={draft.evidence_url}
            onChange={(e) => setDraft({ ...draft, evidence_url: e.target.value })}
            data-testid="input-evidence-url" />
          <Button onClick={submit} disabled={submitting}
            className="bg-emerald-500 hover:bg-emerald-400 text-slate-950"
            data-testid="submit-action-btn">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" /> Log this win</>}
          </Button>
        </CardContent>
      </Card>

      {/* View toggle */}
      <div className="flex items-center gap-2 flex-wrap">
        <Button variant={view === 'current' ? 'default' : 'outline'} size="sm"
          onClick={() => setView('current')} data-testid="view-current-btn">
          Last 30 days
        </Button>
        <Button variant={view === 'all_time' ? 'default' : 'outline'} size="sm"
          onClick={() => setView('all_time')} data-testid="view-all-time-btn">
          All-time
        </Button>
        <span className="mx-1 text-slate-300">·</span>
        <Button variant={verifiedOnly ? 'default' : 'outline'} size="sm"
          onClick={() => setVerifiedOnly((v) => !v)}
          className={verifiedOnly ? 'bg-emerald-500 hover:bg-emerald-400 text-slate-950' : ''}
          data-testid="view-verified-only-btn">
          <ShieldCheck className="w-3.5 h-3.5 mr-1" />
          {verifiedOnly ? 'Verified only · ON' : 'Verified only · OFF'}
        </Button>
        {verifiedOnly && (
          <span className="text-[11px] text-emerald-500 italic">CFO-trusted view — only admin-verified actions shown.</span>
        )}
      </div>

      {/* Leaderboard */}
      {loading ? (
        <div className="text-center py-12 text-slate-300"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Loading…</div>
      ) : board.length === 0 ? (
        <Card><CardContent className="py-12 text-center text-slate-300">
          No savings logged yet. Be the first — log a win above and let the team see it.
        </CardContent></Card>
      ) : (
        <Card>
          <CardHeader><CardTitle className="text-base">Leaderboard</CardTitle></CardHeader>
          <CardContent className="space-y-2" data-testid="leaderboard">
            {board.slice(0, 3).map((entry, i) => {
              const meta = PODIUM_META[i];
              return (
                <div key={entry.engineer_email}
                  className={`p-4 rounded-lg border ${meta.bg} flex items-center gap-4`}
                  data-testid={`podium-${meta.rank}`}>
                  <div className="text-3xl">{meta.label}</div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold truncate">{entry.engineer_name || entry.engineer_email}</p>
                    <p className="text-xs text-slate-300 truncate">
                      {entry.engineer_email} · {entry.actions_count} action(s)
                      {entry.verified_count > 0 && (
                        <span className="ml-1.5 text-emerald-500">· {entry.verified_count} verified ✓</span>
                      )}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-2xl font-bold tabular-nums text-emerald-500">{fmtMoney(entry.monthly_savings_total_usd)}/mo</p>
                    <p className="text-xs text-slate-300">≈ {fmtMoney(entry.annualized_savings_total_usd)}/yr</p>
                  </div>
                </div>
              );
            })}
            {board.length > 3 && (
              <div className="border-t pt-2 mt-3">
                {board.slice(3).map((entry, i) => (
                  <div key={entry.engineer_email}
                    className="flex items-center gap-3 p-2 hover:bg-muted/40 rounded">
                    <span className="text-slate-300 tabular-nums w-6 text-right text-sm">{i + 4}.</span>
                    <span className="flex-1 truncate text-sm">{entry.engineer_name || entry.engineer_email}</span>
                    <span className="text-emerald-500 font-mono font-semibold tabular-nums text-sm">{fmtMoney(entry.monthly_savings_total_usd)}/mo</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Recent actions */}
      <Card>
        <CardHeader><CardTitle className="text-base">Recent actions</CardTitle><CardDescription>Verify what looks legit so it counts toward "verified savings".</CardDescription></CardHeader>
        <CardContent className="space-y-2" data-testid="actions-list">
          {actions.length === 0 ? (
            <p className="text-sm text-slate-300 py-4">No actions yet.</p>
          ) : actions.map((a) => (
            <div key={a.id} className="flex items-start gap-3 p-3 rounded-md border border-border" data-testid={`action-row-${a.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <span className="font-semibold text-sm truncate">{a.engineer_name || a.engineer_email}</span>
                  <Badge variant="outline" className="text-[11px]">{a.action_type.replace(/_/g, ' ')}</Badge>
                  {a.verified_at && <Badge className="text-[11px] bg-emerald-500/15 text-emerald-500 border-emerald-500/30">verified</Badge>}
                </div>
                <p className="text-sm text-slate-300 line-clamp-2">{a.description}</p>
                {a.evidence_url && (
                  <a href={a.evidence_url} target="_blank" rel="noopener noreferrer"
                     className="text-xs text-emerald-500 hover:underline inline-flex items-center gap-1 mt-1"
                     data-testid={`evidence-link-${a.id}`}>
                    <ExternalLink className="w-3 h-3" /> Evidence
                  </a>
                )}
              </div>
              <div className="text-right shrink-0">
                <p className="text-sm font-bold tabular-nums text-emerald-500">{fmtMoney(a.monthly_savings_usd)}/mo</p>
                <div className="flex gap-1 mt-1 justify-end">
                  {!a.verified_at && (
                    <Button size="sm" variant="outline"
                      className="h-7 text-xs border-emerald-500/40 text-emerald-500 hover:bg-emerald-500/10"
                      onClick={() => verify(a.id)} data-testid={`verify-btn-${a.id}`}>
                      <ShieldCheck className="w-3 h-3 mr-0.5" /> Verify
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => remove(a.id)}
                    className="h-7 w-7 p-0 text-slate-300 hover:text-rose-500" data-testid={`delete-btn-${a.id}`}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default EngineerSavingsWall;
